import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteMailConfig, fetchMailConfig, saveMailConfig, testMailConfig } from "../../api/newsletterApi";

const PRESETS = [
  { id: "gmail", label: "Gmail", host: "smtp.gmail.com", port: 465 },
  { id: "outlook", label: "Outlook / Microsoft 365", host: "smtp.office365.com", port: 587 },
  { id: "hostinger", label: "Hostinger", host: "smtp.hostinger.com", port: 465 },
  { id: "ovh", label: "OVH", host: "ssl0.ovh.net", port: 465 },
];

const EMPTY = {
  host: "",
  port: "587",
  username: "",
  password: "",
  from_name: "",
  from_address: "",
  reply_to: "",
  include_staff: false,
};

function formFromConfig(config) {
  if (!config) return EMPTY;
  return {
    host: config.host || "",
    port: String(config.port || 587),
    username: config.username || "",
    password: "",
    from_name: config.from_name || "",
    from_address: config.from_address || "",
    reply_to: config.reply_to || "",
    include_staff: Boolean(config.include_staff),
  };
}

export function NewsletterMailSettings({ onChanged }) {
  const { t, i18n } = useTranslation();
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [encryptionReady, setEncryptionReady] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchMailConfig()
      .then((data) => {
        if (cancelled) return;
        setConfig(data?.config || null);
        setForm(formFromConfig(data?.config));
        setEncryptionReady(data?.encryption_ready !== false);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const set = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const applyPreset = (preset) => {
    setForm((prev) => ({ ...prev, host: preset.host, port: String(preset.port) }));
  };

  const run = async (action, success) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const data = await action();
      if (data && "config" in data) {
        setConfig(data.config);
        setForm(formFromConfig(data.config));
      }
      setNotice(typeof success === "function" ? success(data) : success);
      onChanged?.();
    } catch (err) {
      setError(err.message);
      fetchMailConfig()
        .then((data) => setConfig(data?.config || null))
        .catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const onSave = (event) => {
    event.preventDefault();
    run(
      () => saveMailConfig({ ...form, port: Number(form.port), secure: Number(form.port) === 465 }),
      t("newsletterMailSaved", "Mail settings saved. Send a test email to check them.")
    );
  };

  const verified = config?.verified_at
    ? new Date(config.verified_at).toLocaleString(i18n.language)
    : "";

  return (
    <section className="pingflo-setup">
      <h2>{t("newsletterMailTitle", "Sending email account")}</h2>
      <p className="pingflo-quiet">
        {t(
          "newsletterMailHelp",
          "The newsletter is sent from the club's own mailbox. For Gmail or Outlook, create an app password in the account's security settings and paste it here instead of the normal password."
        )}
      </p>
      {!encryptionReady ? (
        <p className="pingflo-error">
          {t(
            "newsletterMailNoEncryption",
            "The server cannot store mail passwords yet (missing encryption key). Ask the platform administrator."
          )}
        </p>
      ) : null}
      <div className="pingflo-presets">
        {PRESETS.map((preset) => (
          <button key={preset.id} type="button" onClick={() => applyPreset(preset)} disabled={busy}>
            {preset.label}
          </button>
        ))}
      </div>
      <form onSubmit={onSave}>
        <div className="pingflo-row">
          <label>
            {t("newsletterMailHost", "SMTP server")}
            <input value={form.host} onChange={set("host")} placeholder="smtp.example.com" required />
          </label>
          <label className="pingflo-narrow">
            {t("newsletterMailPort", "Port")}
            <input type="number" min="1" max="65535" value={form.port} onChange={set("port")} required />
          </label>
        </div>
        <label>
          {t("newsletterMailUser", "Mailbox user name")}
          <input value={form.username} onChange={set("username")} autoComplete="off" required />
        </label>
        <label>
          {t("newsletterMailPassword", "Password or app password")}
          <input
            type="password"
            value={form.password}
            onChange={set("password")}
            autoComplete="new-password"
            placeholder={
              config?.has_password ? t("newsletterMailPasswordKept", "Stored. Leave empty to keep it.") : ""
            }
          />
        </label>
        <div className="pingflo-row">
          <label>
            {t("newsletterMailFromName", "Sender name")}
            <input value={form.from_name} onChange={set("from_name")} placeholder={t("newsletterClub", "Club")} />
          </label>
          <label>
            {t("newsletterMailFromAddress", "Sender address")}
            <input type="email" value={form.from_address} onChange={set("from_address")} placeholder={form.username} />
          </label>
        </div>
        <label>
          {t("newsletterMailReplyTo", "Replies go to (optional)")}
          <input type="email" value={form.reply_to} onChange={set("reply_to")} />
        </label>
        <label className="pingflo-check">
          <input type="checkbox" checked={form.include_staff} onChange={set("include_staff")} />
          {t("newsletterMailIncludeStaff", "Also send to club admins and coaches")}
        </label>
        {config ? (
          <p className={config.last_error ? "pingflo-error" : "pingflo-quiet"}>
            {config.last_error
              ? `${t("newsletterMailLastError", "Last error")}: ${config.last_error}`
              : verified
                ? `${t("newsletterMailVerified", "Verified")}: ${verified}`
                : t("newsletterMailUnverified", "Not tested yet")}
          </p>
        ) : null}
        {error ? <p className="pingflo-error">{error}</p> : null}
        {notice ? <p className="pingflo-banner">{notice}</p> : null}
        <div className="pingflo-actions">
          <button type="submit" disabled={busy}>
            {t("newsletterMailSave", "Save mail settings")}
          </button>
          <button
            type="button"
            className="is-send"
            disabled={busy || !config}
            onClick={() =>
              run(testMailConfig, (data) =>
                t("newsletterMailTestSent", {
                  email: data?.sent_to || "",
                  defaultValue: "Test email sent to {{email}}",
                })
              )
            }
          >
            {t("newsletterMailTest", "Send test email")}
          </button>
          {config ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (window.confirm(t("newsletterMailRemoveConfirm", "Remove the club mailbox settings?"))) {
                  run(deleteMailConfig, t("newsletterMailRemoved", "Mail settings removed"));
                }
              }}
            >
              {t("newsletterMailRemove", "Remove")}
            </button>
          ) : null}
        </div>
      </form>
    </section>
  );
}
